/**
 * Greptile-style right-side header icons. Three buttons in the top
 * right of the content pane:
 *  - external link → opens /repositories in a new tab
 *  - GitHub mark   → deep-links to /app/install (re-install the GitHub App)
 *  - avatar        → opens a small menu with the current org + placeholder
 *                    items (Switch org, Sign out) — all decorative for v1.
 *
 * The avatar menu is a controlled popover rendered inline (no portal) so
 * tests can query it without setting up a new role="dialog" wrapper.
 * Clicking outside the menu or the avatar closes it.
 */
import { useEffect, useRef, useState } from 'react';

function ExternalLinkIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M9 2h5v5M14 2L7 9M11 9v4H3V5h4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 0C3.58 0 0 3.58 0 8a8 8 0 005.47 7.59c.4.07.55-.17.55-.38v-1.34c-2.23.48-2.7-1.07-2.7-1.07-.36-.92-.89-1.16-.89-1.16-.73-.5.06-.49.06-.49.8.06 1.22.83 1.22.83.72 1.23 1.88.88 2.34.67.07-.52.28-.88.51-1.08-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.13 0 0 .67-.21 2.2.82a7.6 7.6 0 014 0c1.53-1.03 2.2-.82 2.2-.82.44 1.11.16 1.93.08 2.13.51.56.82 1.28.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.74.54 1.5v2.22c0 .21.15.46.55.38A8 8 0 0016 8c0-4.42-3.58-8-8-8z" />
    </svg>
  );
}

function AvatarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <circle cx="8" cy="5" r="3" />
      <path d="M2 14c0-3.31 2.69-6 6-6s6 2.69 6 6" />
    </svg>
  );
}

export function HeaderIcons() {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click so the dropdown doesn't linger after the
  // user clicks elsewhere in the page.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (e.target instanceof Node && wrapperRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div
      className="feluda-header-icons"
      ref={wrapperRef}
      aria-label="Workspace actions"
    >
      <a
        className="feluda-header-icon"
        href="/repositories"
        target="_blank"
        rel="noreferrer noopener"
        title="Open in new tab"
        data-testid="header-icon-open"
      >
        <ExternalLinkIcon />
      </a>
      <a
        className="feluda-header-icon"
        href="/app/install"
        title="Install Feluda on another repo"
        data-testid="header-icon-install"
      >
        <GitHubIcon />
      </a>
      <button
        type="button"
        className="feluda-header-icon feluda-header-avatar"
        data-testid="header-icon-avatar"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="Account"
      >
        <AvatarIcon />
      </button>
      {open && (
        <ul
          className="feluda-header-menu"
          role="menu"
          data-testid="header-menu"
        >
          <li role="presentation" className="feluda-header-menu-heading">
            poridhi / Admin
          </li>
          <li
            role="menuitem"
            className="feluda-header-menu-item feluda-header-menu-disabled"
            aria-disabled="true"
          >
            Switch org
          </li>
          <li
            role="menuitem"
            className="feluda-header-menu-item feluda-header-menu-disabled"
            aria-disabled="true"
          >
            Sign out
          </li>
        </ul>
      )}
    </div>
  );
}
